// @ts-check
/**
 * I18N-01: user-facing text comes from one catalogue. `t(key, params)` fills
 * `{name}` placeholders; a value may be an object of plural forms chosen with
 * Intl.PluralRules from `params.count`. Screens move their strings here as they
 * are rebuilt; a missing key shows the key itself so it is easy to spot.
 */
const en = {
  "approval.askRun": "{who} wants to run a command",
  "approval.askChange": "{who} wants to change {path}",
  "approval.someBunny": "A bunny",
  "approval.aFile": "a file",
  "approval.where": "In {workspace}",
  "approval.expires": "expires {when}",
  "approval.allowOnce": "Allow once",
  "approval.allowChange": "Allow this change",
  "approval.runNow": "Run it now",
  "approval.deny": "Deny",
  "approval.details": "Details",
  "approval.output": "Output",
  "approval.cannotDecide":
    "Only the person who started this run, or an admin, can decide.",
  "approval.state.pending": "Waiting for you",
  "approval.state.approved": "Allowed",
  "approval.state.rejected": "Denied",
  "approval.state.expired": "Expired",
  "approval.state.executed": "Done",
  "approval.state.failed": "Failed",
  "approval.state.resumable": "Allowed · waiting to run",
  // ADM-04: the admin Overview.
  "overview.title": "Overview",
  "overview.description":
    "How your team's bunnies, work and workspace are doing. Counts update as things happen.",
  "overview.unavailable": "The overview could not be loaded.",
  "overview.tiles": "At a glance",
  "overview.tile.runsToday": "Runs today",
  "overview.tile.runsWeek": "Runs, last 7 days",
  "overview.tile.attention": "Needs you",
  "overview.tile.bunnies": "Active bunnies",
  "overview.tile.work": "Open work",
  "overview.tile.automations": "Automations on",
  "overview.tile.monitors": "Healthy monitors",
  "overview.tile.usage": "Model tokens, 7 days",
  "overview.tile.members": "Members",
  "overview.tile.health": "Health",
  "overview.failed": { one: "{count} failed", other: "{count} failed" },
  "overview.running": { one: "{count} running", other: "{count} running" },
  "overview.noFailures": "No failures",
  "overview.failRate": "{count} failed · {rate}%",
  "overview.attentionWaiting": {
    one: "{count} item waiting on you",
    other: "{count} items waiting on you",
  },
  "overview.allClear": "All clear",
  "overview.bunniesDetail": "{working} working now · {paused} paused",
  "overview.workDetail": "{done} done this week · {overdue} overdue",
  "overview.automationsNone": "None set up yet",
  "overview.automationsFailing": {
    one: "{count} failing",
    other: "{count} failing",
  },
  "overview.nextDue": "Next {when}",
  "overview.nothingDue": "Nothing scheduled",
  "overview.monitorsNone": "No monitors yet",
  "overview.monitorsUnhealthy": {
    one: "{count} needs a look",
    other: "{count} need a look",
  },
  "overview.monitorsOf": "of {count} switched on",
  "overview.cost": "{cost} at list prices",
  "overview.costNotTracked": "Cost not reported",
  "overview.invites": {
    one: "{count} pending invite",
    other: "{count} pending invites",
  },
  "overview.backupNotConfigured": "Backups not configured",
  "overview.backupOff": "Off",
  "overview.backupNever": "Never",
  "overview.backupThreshold": "Warns when older than {after}",
  "overview.backupConfigure":
    "Set backup.directory in the server config to turn them on.",
  "overview.ready": "Ready",
  "overview.warnings": { one: "{count} warning", other: "{count} warnings" },
  "overview.noWarnings": "Doctor finds nothing to fix",
  "overview.chart.title": "Runs, last 7 days",
  "overview.chart.description":
    "Bunny runs from chats, automations and delegations, by day.",
  "overview.chart.completed": "Completed",
  "overview.chart.failed": "Failed",
  "overview.chart.today": "Today",
  "overview.chart.day": "Day",
  "overview.chart.summary":
    "{total} runs over the last 7 days: {completed} completed and {failed} failed. Busiest day: {busiest} with {most}.",
  "overview.chart.bar": "{day}: {completed} completed, {failed} failed",
  "overview.empty.runs": "No runs yet — start a chat",
  "overview.empty.runsBody":
    "When bunnies reply, run automations or hand off work, each run shows up here by day.",
  "overview.startChat": "Start a chat",
  "overview.needsYou": "Needs you",
  "overview.needsYouDescription": "The newest items waiting on you.",
  "overview.viewAll": "View all",
  "overview.empty.needsYou": "Nothing needs you",
  "overview.empty.needsYouBody":
    "Approvals, reviews and failures that need a decision appear here.",
  "overview.failing": "Failing automations and monitors",
  "overview.failingDescription":
    "Switched-on automations whose last run failed, and monitors that are not healthy.",
  "overview.empty.failing": "Everything is running",
  "overview.empty.failingBody":
    "Failed automation runs and unhealthy monitors appear here.",
  "overview.kind.automation": "Automation",
  "overview.kind.monitor": "Monitor",
  // FLOW-04: usage against provider limits.
  "overview.limits": "Model usage and limits",
  "overview.limitsDescription":
    "Tokens each model's provider reported this week, and any limit the provider has in force now.",
  "overview.limits.tokens": "{count} tokens",
  "overview.limits.unknown": "Usage not reported",
  "overview.limits.someUnknown": {
    one: "{count} call did not report usage",
    other: "{count} calls did not report usage",
  },
  "overview.limits.inOut": "{input} in · {output} out",
  "overview.limits.limited": "Limited",
  "overview.limits.clear": "No limit in force",
  "overview.limits.resets": "Resets {when}",
  "overview.limits.resetsUnknown": "Reset time not given; retrying {when}",
  "overview.limits.scope.provider": "Every {provider} model",
  "overview.limits.scope.model": "This model only",
  "overview.empty.limits": "No model usage yet",
  "overview.empty.limitsBody":
    "Usage appears here once a provider reports it for a run.",
  "overview.activity": "Recent activity",
  "overview.activityDescription": "The latest bunny runs across the team.",
  "overview.empty.activity": "No activity yet",
  "overview.empty.activityBody":
    "Runs from chats, automations and delegations appear here as they happen.",
  "overview.source.chat": "from a chat",
  "overview.source.automation": "from an automation",
  "overview.source.delegation": "from a delegation",
  "overview.status.running": "Running",
  "overview.status.completed": "Completed",
  "overview.status.failed": "Failed",
  "overview.status.cancelled": "Cancelled",
  "overview.status.unknown": "Interrupted",
  "overview.attention.approval": "Approval",
  "overview.attention.review": "Review",
  "overview.attention.blocked": "Blocked",
  "overview.attention.error": "Failed",
  "overview.attention.unknown": "Unclear",
  "overview.attention.question": "Question",
  "overview.attention.sign_in": "Sign in",
  "overview.models": "Top models: {models}",
  // OPS-01..03: capacity, disk, backups, spend, storage and support export.
  "overview.tile.spend": "Spend against budget",
  "overview.tile.nodes": "Remote nodes online",
  "overview.tile.disk": "Disk free",
  "overview.tile.backup": "Last backup",
  "overview.unknown": "Unknown",
  "overview.spendNone": "No budget",
  "overview.spendSet": "Set one in Spending",
  "overview.spendOf": "of {limit} · {name}, {period}",
  "overview.spendMore": {
    one: "{count} more budget below",
    other: "{count} more budgets below",
  },
  "overview.spendReserved": "{amount} reserved for calls in flight",
  "overview.period.day": "today",
  "overview.period.week": "this week",
  "overview.period.month": "this month",
  "overview.nodesNone": "None registered",
  "overview.nodesSlots": "{free} of {capacity} run slots free",
  "overview.nodesOffline": {
    one: "{count} node offline",
    other: "{count} nodes offline",
  },
  "overview.nodesDisk": "Least disk free on a node: {size}",
  "overview.diskShare": "{percent}% of {total}",
  "overview.diskUnknown": "Free space could not be read",
  "overview.budgets": "Spend budgets",
  "overview.budgetsDescription":
    "Settled spend plus reservations for calls in flight, against each limit.",
  "overview.budgetsManage": "Manage",
  "overview.budgetLine":
    "{spent} spent · {reserved} reserved · of {limit} · resets {when}",
  "overview.budgetMeter": "{percent}% of the budget used or reserved",
  "overview.budgetState.ok": "Within budget",
  "overview.budgetState.near": "Nearly used",
  "overview.budgetState.over": "Used up",
  "overview.unpriced": {
    one: "{count} model call this week had no price, so its spend is unknown.",
    other:
      "{count} model calls this week had no price, so their spend is unknown.",
  },
  "overview.empty.budgets": "No spend budgets",
  "overview.empty.budgetsBody":
    "Without a budget, runs are never held back by cost. Set one in Spending.",
  "overview.storage": "Storage",
  "overview.storageDescription":
    "What this team keeps, measured {when}. Links are never followed.",
  "overview.storage.attachments": "Attachments",
  "overview.storage.artifacts": "Artifacts",
  "overview.storage.worktrees": "Worktrees and mirrors",
  "overview.storage.database": "Conversation stores",
  "overview.storageFiles": { one: "{count} item", other: "{count} items" },
  "overview.storageUnmeasured": "Not visible from the server",
  "overview.storageProject": "Project",
  "overview.storageShared": "Shared database",
  "overview.storageSharedDetail": "Every team's records",
  "overview.storageTruncated":
    "Some folders were too large to measure in full; totals are at least this much.",
  "overview.storageLinks": {
    one: "{count} link was skipped.",
    other: "{count} links were skipped.",
  },
  "overview.export": "Support export",
  "overview.exportDescription":
    "A redacted file for support: versions, readiness, migrations, storage totals and recent errors. Preview it, then download it within 15 minutes.",
  "overview.export.prepare": "Prepare support export",
  "overview.export.failed": "The support export could not be prepared.",
  "overview.export.meta":
    "{size} · {redactions} redactions · download expires {when}",
  "overview.export.never": "Never included:",
  "overview.export.previewLabel": "Support export contents",
  "overview.export.download": "Download support export",
  "project.bunnies": "Bunnies · {count}",
  "project.manage": "Manage",
  "project.openWork": {
    one: "Open work · {count} item",
    other: "Open work · {count} items",
  },
  "project.openBoard": "Open board",
  "project.recent": "Recent activity",
  "project.viewAll": "View all",
  "project.readiness": "Readiness",
  "project.setUp": "Set up",
  "project.noBunnies": "No bunnies yet. Add one to share work.",
  "project.noActivity":
    "Nothing yet. Messages, decisions and results appear here.",
  "project.countsUnavailable": "Work counts are unavailable.",
  "project.check.bunny": "At least one bunny is assigned",
  "project.check.instructions": "Shared instructions are written",
  "project.check.workspace": "A shared workspace is attached",
  "project.check.connections": "Connections are granted",
  "project.empty.title": "Bring bunnies into this project",
  "project.empty.body":
    "Assign bunnies, then plan work on the project's board. Everything they share stays inside the project.",
  "project.empty.action": "Add bunnies",
  // SET-04: Members page and invites.
  "members.title": "Members",
  "members.description":
    "People in this team, their roles and how they sign in. Invite people with a link; removing someone keeps their account and history.",
  "members.listLabel": "Team members",
  "members.people": "People",
  "members.invite": "Invite people",
  "members.pending": "Pending invites",
  "members.pendingLabel": "Pending invites",
  "members.pendingDescription":
    "Links that haven't been used yet. Resend creates a new link; the old one stops working.",
  "members.loadFailed": "Couldn't load the team's members. Try again.",
  "members.changeFailed": "Couldn't update the team. Try again.",
  "members.empty":
    "No one else is in this team yet. Invite people to get started.",
  "members.you": "(you)",
  "members.role.owner": "Owner",
  "members.role.admin": "Admin",
  "members.role.member": "Member",
  "members.role.viewer": "Viewer",
  "members.roleHint.owner": "Everything, including other owners",
  "members.roleHint.admin": "Manages the team, members and settings",
  "members.roleHint.member": "Chats, works and uses shared knowledge",
  "members.roleHint.viewer": "Reads without making changes",
  "members.roleFor": "Role for {name}",
  "members.actionsFor": "Actions for {name}",
  "members.mfaOn": "Two-step on",
  "members.mfaOff": "Two-step off",
  "members.lastSeen": "Active {when}",
  "members.neverSignedIn": "Never signed in",
  "members.makeOwnerTitle": "Make {name} an owner?",
  "members.makeOwnerBody":
    "Owners can change other owners and remove them. You can change this later.",
  "members.makeOwner": "Make owner",
  "members.roleChanged": "{name} is now {role}.",
  "members.remove": "Remove from team",
  "members.removeTitle": "Remove {name} from the team?",
  "members.removeBody":
    "They lose access to this team now, and every browser they're signed in to is signed out. Their account and history stay; you can invite them again.",
  "members.removed": "{name} was removed from the team.",
  "members.signOut": "Sign out everywhere",
  "members.signOutTitle": "Sign out {name} everywhere?",
  "members.signOutBody":
    "All of their browser sessions end now. Their account, access and API tokens are unchanged.",
  "members.signedOut": {
    one: "{name}: {count} session signed out.",
    other: "{name}: {count} sessions signed out.",
  },
  "members.resetLink": "Create a password reset link",
  "members.resetMfa": "Reset two-step sign-in",
  "members.resend": "Resend",
  "members.resendFor": "Resend invite for {who}",
  "members.revoke": "Revoke",
  "members.revokeFor": "Revoke invite for {who}",
  "members.revokeTitle": "Revoke this invite?",
  "members.revokeBody": "The link stops working. No one can join with it.",
  "members.revoked": "Invite revoked.",
  "members.cancel": "Cancel",
  "members.done": "Done",
  "members.invite.title": "Invite people",
  "members.invite.description":
    "Choose the role they join with and how long the link works. Each link adds one person.",
  "members.invite.role": "Role",
  "members.invite.expiry": "Link expires after",
  "members.invite.days": { one: "{count} day", other: "{count} days" },
  "members.invite.email": "Email address",
  "members.invite.emailHint":
    "We'll email the link. Leave empty to copy the link yourself.",
  "members.invite.emailInvalid":
    "Enter an email address like name@example.com.",
  "members.invite.linkOnly":
    "You'll get a link to share. Anyone with it can join once, until it expires.",
  "members.invite.createLink": "Create invite link",
  "members.invite.send": "Send invite",
  "members.invite.ready": "Invite link ready",
  "members.invite.shownOnce":
    "Share this link with the person you're inviting. It's shown only once, works once, and adds them as {role} until {date}.",
  "members.invite.emailed":
    "We're emailing this link to {email}. It works once and adds them as {role} until {date}. You can also copy it now; it isn't shown again.",
  "members.invite.link": "Invite link",
  "members.invite.copy": "Copy link",
  "members.invite.copied": "Invite link copied.",
  "members.invite.copyManually":
    "Couldn't copy. The link is selected; copy it yourself.",
  "members.invite.linkInvite": "Invite link",
  "members.invite.by": "Invited by {name}",
  "members.invite.expires": "Expires {when}",
  "members.invite.expired": "Expired",
  "members.local.open": "Create local user",
  "members.local.title": "Create a local user",
  "members.local.description":
    "Creates an account with a password you choose and share privately. Prefer invites, so people choose their own password.",
  "members.local.role": "Team role",
  "members.local.create": "Create user",
  "members.local.created":
    "{name} can now sign in with that email and the password you chose.",
  "invite.title": "Join {team}",
  "invite.invitedBy": "{who} invited you as {role}.",
  "invite.invited": "You're invited as {role}.",
  "invite.role.owner": "an owner",
  "invite.role.admin": "an admin",
  "invite.role.member": "a member",
  "invite.role.viewer": "a viewer",
  "invite.displayName": "Your name",
  "invite.password": "Password",
  "invite.confirmPassword": "Confirm password",
  "invite.passwordRule":
    "At least 8 characters. No uppercase, number, or symbol requirements.",
  "invite.mismatch": "Passwords don't match.",
  "invite.email": "Email address",
  "invite.createAccount": "Create account and join",
  "invite.haveAccount": "I already have an account",
  "invite.signInToJoin": "Sign in to join {team}.",
  "invite.joinTitle": "Join {team}?",
  "invite.join": "Join team",
  "invite.joined": "You joined {team}.",
  "invite.welcome": "Welcome to {team}.",
  // IAM-08: creating teams.
  "teams.title": "Create another team",
  "teams.description":
    "A separate team with its own bunnies, members and settings. You become its owner, and can switch teams from the team menu.",
  "teams.name": "Team name",
  "teams.new": "New team",
  "teams.create": "Create team",
  "teams.created": "{name} is ready. You're its owner.",
  "teams.nameInvalid": "Enter a team name of up to 100 characters.",
};

/** @type {Record<string, string | Record<string, string>>} */
let catalogue = en;
let locale = "en";
let plurals = new Intl.PluralRules(locale);

/** Switch catalogues (other languages plug in here). @param {string} next @param {Record<string, string | Record<string, string>>} entries */
export function setCatalogue(next, entries) {
  locale = next;
  catalogue = { ...en, ...entries };
  plurals = new Intl.PluralRules(locale);
}

/** @param {string} key @param {Record<string, string | number>} [params] */
export function t(key, params = {}) {
  const entry = catalogue[key];
  const text =
    typeof entry === "object"
      ? (entry[plurals.select(Number(params.count ?? 0))] ?? entry.other)
      : entry;
  if (text === undefined) return key;
  return text.replace(/\{(\w+)\}/gu, (match, name) =>
    Object.hasOwn(params, name) ? String(params[name]) : match,
  );
}
